import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { LISTING_STATE_PUBLISHED, LISTING_STATE_CLOSED } from '../../util/types';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { getFavoriteIds } from '../../ducks/favorites.duck';

const { UUID } = sdkTypes;

// listings/query does NOT support an 'ids' filter (verified: only author_id, pub_*,
// meta_*, price, keywords are supported). Favorite listings are fetched with N
// parallel sdk.listings.show() calls instead, throttled in small batches so a long
// favorites list doesn't fire hundreds of requests at once.
const SHOW_BATCH_SIZE = 20;

const chunk = (array, size) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

// ================ Async Thunks ================ //

////////////////////////////
// Fetch Favorite Listings //
////////////////////////////
const fetchFavoriteListingsPayloadCreator = (
  { favoriteIds, config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  if (favoriteIds.length === 0) {
    return Promise.resolve([]);
  }

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;
  const params = {
    // currentStock is what tells a SOLD listing apart from an available one:
    // a sale leaves the listing 'published' and only drops its stock to 0.
    // FavoritesPage greys those cards out (badge "Vendu").
    include: ['author', 'images', 'currentStock'],
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };

  const batches = chunk(favoriteIds, SHOW_BATCH_SIZE);

  return batches
    .reduce(
      (previousBatches, batchIds) =>
        previousBatches.then(previousResults =>
          Promise.allSettled(
            batchIds.map(id => sdk.listings.show({ id: new UUID(id), ...params }))
          ).then(results => [...previousResults, ...results])
        ),
      Promise.resolve([])
    )
    .then(results => {
      // A 404 means the listing was deleted — drop it silently. Any other
      // rejection (429 rate-limit, timeout, 5xx) means we fetched an INCOMPLETE
      // list; surface it as a page error rather than silently hiding favorites,
      // which the user would misread as "my favorite was deleted".
      const hardFailure = results.find(
        result => result.status === 'rejected' && (result.reason?.status ?? 0) !== 404
      );
      if (hardFailure) {
        return rejectWithValue(storableError(hardFailure.reason));
      }

      const fulfilledResponses = results
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value);

      fulfilledResponses.forEach(response => dispatch(addMarketplaceEntities(response)));

      // Dropped: only dead ids (deleted listing, or a 404 above).
      // Kept, both greyed with a badge on FavoritesPage (Mathieu's call, 01/08):
      //   - withdrawn by the seller -> state 'closed'                  -> "Plus disponible"
      //   - sold                    -> still 'published', stock 0      -> "Vendu"
      const availableListingsById = new Map(
        fulfilledResponses
          .map(response => response.data.data)
          .filter(
            listing =>
              !listing.attributes.deleted &&
              (listing.attributes.state === LISTING_STATE_PUBLISHED ||
                listing.attributes.state === LISTING_STATE_CLOSED)
          )
          .map(listing => [listing.id.uuid, listing])
      );

      // favoriteIds is already ordered most-recently-added-first (toggleFavorite
      // prepends), so reusing that order also orders the display.
      return favoriteIds
        .filter(id => availableListingsById.has(id))
        .map(id => ({ id: availableListingsById.get(id).id, type: 'listing' }));
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchFavoriteListingsThunk = createAsyncThunk(
  'FavoritesPage/fetchFavoriteListings',
  fetchFavoriteListingsPayloadCreator
);

// ================ Slice ================ //

const favoritesPageSlice = createSlice({
  name: 'FavoritesPage',
  initialState: {
    listingRefs: [],
    fetchFavoriteListingsInProgress: false,
    fetchFavoriteListingsError: null,
  },
  reducers: {
    // Surfaces a failure that happens before the fetch thunk runs (e.g.
    // fetchCurrentUser rejecting in loadData), so the page shows the error
    // state instead of a false "no favorites yet" empty state.
    favoritesFetchFailed: (state, action) => {
      state.fetchFavoriteListingsInProgress = false;
      state.fetchFavoriteListingsError = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchFavoriteListingsThunk.pending, state => {
        state.fetchFavoriteListingsInProgress = true;
        state.fetchFavoriteListingsError = null;
      })
      .addCase(fetchFavoriteListingsThunk.fulfilled, (state, action) => {
        state.fetchFavoriteListingsInProgress = false;
        state.listingRefs = action.payload;
      })
      .addCase(fetchFavoriteListingsThunk.rejected, (state, action) => {
        state.fetchFavoriteListingsInProgress = false;
        state.fetchFavoriteListingsError = action.payload;
      });
  },
});

export const { favoritesFetchFailed } = favoritesPageSlice.actions;

export default favoritesPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // fetchCurrentUser has a 1s dedup guard: without enforce:true, a currentUser
  // fetched by a previous page could be served from cache here, with stale favorites.
  return dispatch(fetchCurrentUser({ enforce: true }))
    .then(currentUser => {
      const favoriteIds = getFavoriteIds(currentUser);
      return dispatch(fetchFavoriteListingsThunk({ favoriteIds, config }));
    })
    .catch(e => {
      // fetchCurrentUser rejected — surface it as a page error instead of the
      // false "no favorites yet" empty state (the fetch thunk never ran).
      dispatch(favoritesFetchFailed(storableError(e)));
      return Promise.reject(e);
    });
};
